/* eslint-disable react/no-children-prop */
import React from "react";
import { useTranslation, Trans } from "react-i18next";
import { getRouteApi } from "@tanstack/react-router";
//
import {
  SpringOnboardingText,
  SpringOnboardingTitle,
} from "@components/Typography";

import { useTelusEnrollMutation } from "src/backendApi/queries/telus-queries";

import {
  Alert,
  Anchor,
  Box,
  Center,
  Flex,
  Group,
  Stack,
  SimpleGrid,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useColors } from "@utils/useColors";
import { useAppForm, type SelectInputOption } from "@components/form/app-form";
//
import { staticLinks } from "@config/staticLinks";
import { z } from "zod";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { US_STATES } from "@neuromotion-inc/mightier-ts-sdk";

const CA_PROVINCES: Record<string, string> = {
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  NT: "Northwest Territories",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
};

function getProvinceCodeSelectOpts(countryCode: string): SelectInputOption[] {
  const choices = countryCode === "CA" ? CA_PROVINCES : US_STATES;
  return Object.entries(choices).map(([value, label]) => ({ label, value }));
}

const formSchema = z.object({
  firstName: z.string().trim().min(2),
  lastName: z.string().trim().min(2),
  email: z.email(),
  emailOptIn: z.boolean(),
  phoneNum: z
    .string()
    .trim()
    .min(4)
    .refine((val) => isPossiblePhoneNumber(val, "US"), {
      error: "Enter a valid phone number.",
    }),
  addr1: z.string().trim().min(2),
  addr2: z.string().trim(),
  city: z.string().trim().min(2),
  provinceCode: z.string().nonempty({ error: "Please select a valid option." }),
  zip: z.string().trim().min(2),
  country: z.enum(["US", "CA"]),
  employerName: z.string().trim().min(2).max(64),
  agreedToTos: z.boolean().refine((val) => val === true, {
    message: "You must agree to Mightier Terms of Service",
  }),
});

const Route = getRouteApi("/telus/register/enroll");

export function TelusEnrollPage() {
  const { t } = useTranslation();
  const themeColors = useColors();

  const nav = Route.useNavigate();

  const { mutateAsync } = useTelusEnrollMutation({
    onSuccess: (data) => {
      console.log("successful mutation !!!!", data);
      const accountActivationUrl = data.account_activation_url;
      if (accountActivationUrl) {
        window.location.href = accountActivationUrl;
      } else {
        nav({ to: "/telus/thank-you" });
      }
    },
    onError: (err) => {
      console.error(err);
      notifications.show({
        title: t("springHealth.verify.submitNotifications.errorTitle"),
        message: t("springHealth.verify.submitNotifications.errorBody"),
        color: "red",
      });
    },
  });

  const inputStyles = React.useMemo(() => {
    return {
      input: { color: themeColors.royal100 },
    };
  }, [themeColors.royal100]);

  const form = useAppForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      emailOptIn: true,
      phoneNum: "",
      addr1: "",
      addr2: "",
      city: "",
      provinceCode: "",
      zip: "",
      country: "US",
      employerName: "",
      agreedToTos: false,
    },
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      console.log("Telus enroll submitted:", value);
      // Re-parse to get the transformed (trimmed) output
      const validatedData = formSchema.parse(value);

      await mutateAsync({
        payload: {
          employer_name: validatedData.employerName,
          email_address: validatedData.email,
          user: {
            first_name: validatedData.firstName,
            last_name: validatedData.lastName,
            notification_preferences: {
              is_email_ok: validatedData.emailOptIn,
              is_sms_ok: false,
            },
            phonenumber: validatedData.phoneNum,
            shipping_information: {
              address_1: validatedData.addr1,
              address_2: validatedData.addr2,
              city: validatedData.city,
              state: validatedData.provinceCode,
              country: validatedData.country,
              zipcode: validatedData.zip,
            },
          },
        },
      });
    },
  });

  return (
    <Stack px="lg" mt={100}>
      <Center w="100%">
        <SpringOnboardingTitle order={3}>
          {t("telus.enrollPage.pageTitle")}
        </SpringOnboardingTitle>
      </Center>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <SimpleGrid cols={{ xs: 1, md: 2 }} spacing="xl">
          <Stack>
            <Alert color="blue" p="xl" radius={16}>
              <SpringOnboardingText>
                {t("telus.enrollPage.eligibilityConfirmed")}
              </SpringOnboardingText>
            </Alert>

            <Box>
              <SpringOnboardingTitle order={3}>
                {t("springHealth.register.contact.title")}
              </SpringOnboardingTitle>
            </Box>

            <form.AppField
              name="firstName"
              children={(field) => (
                <field.TextField
                  placeholder={t("springHealth.register.contact.firstName")}
                  errors={field.state.meta.errors}
                  autoComplete="given-name"
                  data-testid="first-name"
                  styles={inputStyles}
                />
              )}
            />

            <form.AppField
              name="lastName"
              children={(field) => (
                <field.TextField
                  placeholder={t("springHealth.register.contact.lastName")}
                  errors={field.state.meta.errors}
                  autoComplete="family-name"
                  data-testid="last-name"
                  styles={inputStyles}
                />
              )}
            />

            <form.AppField
              name="email"
              children={(field) => (
                <field.TextField
                  placeholder={t("springHealth.register.contact.emailAddress")}
                  errors={field.state.meta.errors}
                  autoComplete="email"
                  data-testid="email"
                  styles={inputStyles}
                />
              )}
            />

            <form.AppField
              name="emailOptIn"
              children={(field) => (
                <field.Checkbox
                  label={t("springHealth.register.contact.emailOptIn")}
                  c={themeColors.royal100}
                />
              )}
            />

            <form.AppField
              name="phoneNum"
              children={(field) => (
                <field.PhoneNumber
                  label={t("springHealth.register.contact.phoneNumber")}
                  labelProps={{ c: themeColors.royal100 }}
                  styles={inputStyles}
                />
              )}
            />

            <form.AppField
              name="employerName"
              children={(field) => (
                <field.TextField
                  placeholder={t("telus.enrollPage.employer")}
                  labelProps={{ c: themeColors.royal100 }}
                  errors={field.state.meta.errors}
                  data-testid="employer-name"
                  styles={inputStyles}
                />
              )}
            />
          </Stack>

          <Stack>
            <Box>
              <SpringOnboardingTitle order={3}>
                {t("springHealth.register.shipping.title")}
              </SpringOnboardingTitle>
              <SpringOnboardingText>
                {t("springHealth.register.shipping.note")}
              </SpringOnboardingText>
            </Box>

            <form.AppField
              name="country"
              children={(field) => (
                <field.Select
                  label={t("springHealth.register.shipping.country")}
                  labelProps={{ c: themeColors.royal100 }}
                  data={["US", "CA"]}
                  value={field.state.value}
                  onChange={(newVal) => {
                    field.handleChange(newVal);
                    form.setFieldValue("provinceCode", "");
                  }}
                  onBlur={field.handleBlur}
                  data-testid="addr-country"
                />
              )}
            />

            <form.AppField
              name="addr1"
              children={(field) => (
                <field.TextField
                  label={t("springHealth.register.shipping.address")}
                  labelProps={{ c: themeColors.royal100 }}
                  errors={field.state.meta.errors}
                  autoComplete="address-line1"
                  data-testid="addr-addr1"
                  styles={inputStyles}
                />
              )}
            />

            <form.AppField
              name="addr2"
              children={(field) => (
                <field.TextField
                  placeholder={t("springHealth.register.shipping.addressLine2")}
                  errors={field.state.meta.errors}
                  autoComplete="address-line2"
                  data-testid="addr-addr2"
                  styles={inputStyles}
                />
              )}
            />

            <form.Subscribe
              selector={({ values }) => values.country}
              children={(country) => (
                <>
                  <Group>
                    <form.AppField
                      name="city"
                      children={(field) => (
                        <field.TextField
                          placeholder={t("springHealth.register.shipping.city")}
                          errors={field.state.meta.errors}
                          autoComplete="address-level2"
                          data-testid="addr-city"
                          styles={inputStyles}
                        />
                      )}
                    />
                    <form.AppField
                      name="provinceCode"
                      children={(field) => (
                        <field.Select
                          placeholder={t(
                            `springHealth.register.shipping.${country === "CA" ? "province" : "state"}`,
                          )}
                          data={getProvinceCodeSelectOpts(country)}
                          value={field.state.value}
                          onChange={(newVal) => field.handleChange(newVal)}
                          onBlur={field.handleBlur}
                          autoComplete="address-level1"
                          data-testid="addr-provinceCode"
                          styles={inputStyles}
                        />
                      )}
                    />
                  </Group>

                  <form.AppField
                    name="zip"
                    children={(field) => (
                      <field.TextField
                        placeholder={t(
                          `springHealth.register.shipping.${country === "CA" ? "postalCode" : "zipCode"}`,
                        )}
                        errors={field.state.meta.errors}
                        autoComplete="postal-code"
                        data-testid="addr-zip"
                        styles={inputStyles}
                      />
                    )}
                  />
                </>
              )}
            />

            <form.AppField
              name="agreedToTos"
              children={(field) => (
                <field.Checkbox
                  label={
                    <Trans
                      i18nKey="springHealth.register.contact.agreedToTos"
                      components={{
                        1: (
                          <Anchor
                            href={staticLinks.termsOfService}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: "inherit" }}
                            c={themeColors.tufts100}
                          />
                        ),
                      }}
                    />
                  }
                  error={field.state.meta.errors[0]?.message}
                  data-testid="agreed-to-tos"
                  c={themeColors.royal100}
                />
              )}
            />
          </Stack>
        </SimpleGrid>

        <Flex direction="column" align="flex-end" mt="xl" pr="xl" gap="sm">
          <form.Subscribe
            selector={(state) => [
              state.canSubmit,
              state.isSubmitting,
              state.isDefaultValue,
            ]}
            children={([canSubmit, isSubmitting, isDefaultValue]) => (
              <form.SubmitButton
                loading={isSubmitting}
                disabled={!canSubmit || isDefaultValue}
                data-testid="submit-btn"
              >
                {t("loginPage.next")}
              </form.SubmitButton>
            )}
          />
        </Flex>
      </form>
    </Stack>
  );
}
